import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import PortfolioOverview from './components/PortfolioOverview';
import AssetAllocationChart from './components/AssetAllocationChart';
import PerformanceChart from './components/PerformanceChart';
import HoldingsTable from './components/HoldingsTable';
import AnalyticsPanel from './components/AnalyticsPanel';
import TransactionHistory from './components/TransactionHistory';

const PortfolioTracker = () => {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);

  // Mock data
  const portfolioData = {
    totalValue: 125847.32,
    change24h: 3247.85,
    changePercent24h: 2.65,
    bestPerformer: {
      symbol: 'ETH',
      change: 8.42
    },
    worstPerformer: {
      symbol: 'ADA',
      change: -3.21
    }
  };

  const allocationData = [
    { name: 'Bitcoin', value: 52847.32, percentage: 42.0 },
    { name: 'Ethereum', value: 31508.79, percentage: 25.0 },
    { name: 'Binance Coin', value: 18877.10, percentage: 15.0 },
    { name: 'Cardano', value: 12584.73, percentage: 10.0 },
    { name: 'Solana', value: 6292.37, percentage: 5.0 },
    { name: 'Others', value: 3737.01, percentage: 3.0 }
  ];

  const performanceData = {
    '24h': [
      { date: '00:00', value: 122599.47 },
      { date: '04:00', value: 123456.78 },
      { date: '08:00', value: 124123.45 },
      { date: '12:00', value: 125234.56 },
      { date: '16:00', value: 124987.65 },
      { date: '20:00', value: 125847.32 }
    ],
    '7d': [
      { date: 'Mon', value: 118234.56 },
      { date: 'Tue', value: 120456.78 },
      { date: 'Wed', value: 119876.54 },
      { date: 'Thu', value: 122345.67 },
      { date: 'Fri', value: 124567.89 },
      { date: 'Sat', value: 123456.78 },
      { date: 'Sun', value: 125847.32 }
    ],
    '30d': [
      { date: 'Week 1', value: 115234.56 },
      { date: 'Week 2', value: 118456.78 },
      { date: 'Week 3', value: 121876.54 },
      { date: 'Week 4', value: 125847.32 }
    ],
    '90d': [
      { date: 'Month 1', value: 98234.56 },
      { date: 'Month 2', value: 112456.78 },
      { date: 'Month 3', value: 125847.32 }
    ],
    '1y': [
      { date: 'Q1', value: 85234.56 },
      { date: 'Q2', value: 95456.78 },
      { date: 'Q3', value: 108876.54 },
      { date: 'Q4', value: 125847.32 }
    ]
  };

  const holdings = [
    {
      id: 1,
      name: 'Bitcoin',
      symbol: 'BTC',
      icon: 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=32&h=32&fit=crop&crop=center',
      price: 43567.89,
      quantity: 1.213456,
      value: 52847.32,
      change24h: 2.45,
      pnl: 8234.56,
      pnlPercent: 18.45,
      allocation: 42.0
    },
    {
      id: 2,
      name: 'Ethereum',
      symbol: 'ETH',
      icon: 'https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=32&h=32&fit=crop&crop=center',
      price: 2634.52,
      quantity: 11.956789,
      value: 31508.79,
      change24h: 8.42,
      pnl: 5234.78,
      pnlPercent: 19.87,
      allocation: 25.0
    },
    {
      id: 3,
      name: 'Binance Coin',
      symbol: 'BNB',
      icon: 'https://images.unsplash.com/photo-1640340434855-6084b1f4901c?w=32&h=32&fit=crop&crop=center',
      price: 312.45,
      quantity: 60.456789,
      value: 18877.10,
      change24h: -1.23,
      pnl: 2134.56,
      pnlPercent: 12.76,
      allocation: 15.0
    },
    {
      id: 4,
      name: 'Cardano',
      symbol: 'ADA',
      icon: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=32&h=32&fit=crop&crop=center',
      price: 0.456789,
      quantity: 27543.123456,
      value: 12584.73,
      change24h: -3.21,
      pnl: -1234.56,
      pnlPercent: -8.93,
      allocation: 10.0
    },
    {
      id: 5,
      name: 'Solana',
      symbol: 'SOL',
      icon: 'https://images.unsplash.com/photo-1640826844110-c7b8b0c7b2b0?w=32&h=32&fit=crop&crop=center',
      price: 98.76,
      quantity: 63.723456,
      value: 6292.37,
      change24h: 5.67,
      pnl: 892.34,
      pnlPercent: 16.54,
      allocation: 5.0
    }
  ];

  const analyticsData = {
    diversification: {
      assetCount: 5,
      largestHolding: 42.0,
      concentrationRisk: 'Medium',
      herfindahlIndex: 0.285
    },
    risk: {
      level: 'Medium',
      volatility: 18.45,
      valueAtRisk: 12584.73,
      sharpeRatio: 1.34
    },
    correlations: [
      { pair: 'BTC-ETH', value: 0.78 },
      { pair: 'BTC-BNB', value: 0.65 },
      { pair: 'ETH-ADA', value: 0.72 },
      { pair: 'SOL-BNB', value: 0.58 }
    ],
    performance: {
      totalReturn: 23.45,
      annualizedReturn: 18.76,
      maxDrawdown: -15.23,
      winRate: 67.8
    }
  };

  const transactions = [
    {
      id: 1,
      date: '2025-01-12T10:30:00Z',
      type: 'buy',
      asset: 'Bitcoin',
      symbol: 'BTC',
      icon: 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=32&h=32&fit=crop&crop=center',
      quantity: 0.5,
      price: 43200.00,
      total: 21600.00,
      status: 'completed'
    },
    {
      id: 2,
      date: '2025-01-11T14:15:00Z',
      type: 'sell',
      asset: 'Ethereum',
      symbol: 'ETH',
      icon: 'https://images.unsplash.com/photo-1621761191319-c6fb62004040?w=32&h=32&fit=crop&crop=center',
      quantity: 2.0,
      price: 2600.00,
      total: 5200.00,
      status: 'completed'
    },
    {
      id: 3,
      date: '2025-01-10T09:45:00Z',
      type: 'buy',
      asset: 'Cardano',
      symbol: 'ADA',
      icon: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=32&h=32&fit=crop&crop=center',
      quantity: 10000,
      price: 0.45,
      total: 4500.00,
      status: 'completed'
    },
    {
      id: 4,
      date: '2025-01-09T16:20:00Z',
      type: 'transfer',
      asset: 'Solana',
      symbol: 'SOL',
      icon: 'https://images.unsplash.com/photo-1640826844110-c7b8b0c7b2b0?w=32&h=32&fit=crop&crop=center',
      quantity: 50,
      price: 95.00,
      total: 4750.00,
      status: 'pending'
    }
  ];

  const currencyOptions = [
    { value: 'USD', label: 'USD ($)' },
    { value: 'EUR', label: 'EUR (€)' },
    { value: 'BTC', label: 'BTC (₿)' },
    { value: 'ETH', label: 'ETH (Ξ)' }
  ];

  const tabs = [
    { key: 'overview', label: 'Overview', icon: 'BarChart3' },
    { key: 'analytics', label: 'Analytics', icon: 'TrendingUp' },
    { key: 'transactions', label: 'Transactions', icon: 'History' }
  ];

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleExport = () => {
    // Mock export functionality
    console.log('Exporting portfolio data...');
    // In a real app, this would generate and download a CSV/PDF report
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-[60px] pb-[80px] md:pb-0">
          <div className="flex items-center justify-center h-[calc(100vh-60px)]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading portfolio data...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-[60px] pb-[80px] md:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Portfolio Tracker</h1>
              <p className="text-muted-foreground">
                Monitor your cryptocurrency investments with real-time analytics
              </p>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 mt-4 md:mt-0">
              <Select
                options={currencyOptions}
                value={selectedCurrency}
                onChange={setSelectedCurrency}
                className="w-full sm:w-32"
              />
              <Button
                variant="outline"
                onClick={handleExport}
                iconName="Download"
                iconPosition="left"
              >
                Export Report
              </Button>
            </div>
          </div>

          {/* Portfolio Overview */}
          <PortfolioOverview 
            portfolioData={portfolioData} 
            selectedCurrency={selectedCurrency} 
          />

          {/* Tab Navigation */}
          <div className="flex space-x-1 mb-8 bg-muted/50 p-1 rounded-lg">
            {tabs?.map((tab) => (
              <button
                key={tab?.key}
                onClick={() => setActiveTab(tab?.key)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex-1 justify-center ${
                  activeTab === tab?.key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <AssetAllocationChart allocationData={allocationData} />
                <PerformanceChart 
                  performanceData={performanceData} 
                  selectedCurrency={selectedCurrency} 
                />
              </div>
              <HoldingsTable 
                holdings={holdings} 
                selectedCurrency={selectedCurrency}
                onExport={handleExport}
              />
            </div>
          )}

          {activeTab === 'analytics' && (
            <AnalyticsPanel 
              analyticsData={analyticsData} 
              selectedCurrency={selectedCurrency} 
            />
          )}

          {activeTab === 'transactions' && (
            <TransactionHistory 
              transactions={transactions} 
              selectedCurrency={selectedCurrency} 
            />
          )}
        </div>
      </div>
    </>
  );
};

export default PortfolioTracker;